import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  CheckIcon, 
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { SmtpSettings, SmtpFormData } from '../types/Email';
import { emailApi } from '../services/emailApi';
import { useBranch } from '../contexts/BranchContext';
import PermissionGate from './PermissionGate';

interface EmailSettingsModalProps {
  onClose: () => void;
  onSettingsUpdated: () => void;
}

const EmailSettingsModal: React.FC<EmailSettingsModalProps> = ({
  onClose,
  onSettingsUpdated
}) => {
  const { currentBranch, isViewingAllBranches, branches } = useBranch();
  const [smtpSettings, setSmtpSettings] = useState<SmtpSettings[]>([]);
  const [activeSettings, setActiveSettings] = useState<SmtpSettings | null>(null);
  const [providerType, setProviderType] = useState<'smtp' | 'zeptomail'>('smtp');
  const [formData, setFormData] = useState<SmtpFormData>({
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: false,
    smtpUsername: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: '',
    isActive: true,
    branchId: null // NULL = Organization-level, branch ID = Branch-level
  });
  const [zeptoApiKey, setZeptoApiKey] = useState('');
  const [zeptoDomain, setZeptoDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [settingsType, setSettingsType] = useState<'organization' | 'branch'>('organization');
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [useGlobalEmailSetting, setUseGlobalEmailSetting] = useState(true);

  useEffect(() => {
    fetchSmtpSettings();
    fetchGlobalEmailSetting();
  }, [currentBranch, isViewingAllBranches]);

  const fetchSmtpSettings = async () => {
    try {
      const settings = await emailApi.getSmtpSettings();
      setSmtpSettings(settings);
      const active = settings.find(s => s.isActive);
      setActiveSettings(active || null);
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
      setError('Failed to fetch SMTP settings');
    }
  };

  const fetchGlobalEmailSetting = async () => {
    try {
      const setting = await emailApi.getGlobalEmailSetting();
      setUseGlobalEmailSetting(setting.useGlobalEmailSetting);
    } catch (error) {
      console.error('Error fetching global email setting:', error);
    }
  };

  const handleToggleGlobalEmailSetting = async () => {
    try {
      const newValue = !useGlobalEmailSetting;
      await emailApi.updateGlobalEmailSetting(newValue);
      setUseGlobalEmailSetting(newValue);
      setSuccess(newValue ? 'Using global email settings (from .env)' : 'Using organization custom email settings');
    } catch (error) {
      console.error('Error updating global email setting:', error);
      setError('Failed to update global email setting');
    }
  };

  const handleInputChange = (field: keyof SmtpFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEdit = (settings: SmtpSettings) => {
    setProviderType(settings.providerType || 'smtp');
    setFormData({
      smtpHost: settings.smtpHost || '',
      smtpPort: settings.smtpPort || 587,
      smtpSecure: settings.smtpSecure || false,
      smtpUsername: settings.smtpUsername || '',
      smtpPassword: '', // Don't show existing password
      fromEmail: settings.fromEmail,
      fromName: settings.fromName,
      isActive: settings.isActive
    });
    setZeptoApiKey(settings.zeptoApiKey || '');
    setZeptoDomain(settings.zeptoDomain || '');
    setIsEditing(true);
    setEditingId(settings.id);
  };

  const handleCreateNew = () => {
    setProviderType('smtp');
    setFormData({
      smtpHost: '',
      smtpPort: 587,
      smtpSecure: false,
      smtpUsername: '',
      smtpPassword: '',
      fromEmail: '',
      fromName: '',
      isActive: true,
      branchId: settingsType === 'branch' ? selectedBranchId || currentBranch?.id || null : null
    });
    setZeptoApiKey('');
    setZeptoDomain('');
    setIsEditing(true);
    setEditingId(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingId(null);
    setSelectedBranchId(null);
    setFormData({
      smtpHost: '',
      smtpPort: 587,
      smtpSecure: false,
      smtpUsername: '',
      smtpPassword: '',
      fromEmail: '',
      fromName: '',
      isActive: true,
      branchId: null
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Validation based on provider type
      if (providerType === 'smtp') {
        if (!formData.smtpHost || !formData.smtpUsername || !formData.smtpPassword || 
            !formData.fromEmail || !formData.fromName) {
          setError('All SMTP fields are required');
          return;
        }
      } else if (providerType === 'zeptomail') {
        if (!zeptoApiKey || !zeptoDomain || !formData.fromEmail || !formData.fromName) {
          setError('ZeptoMail API key, domain, from email, and from name are required');
          return;
        }
      }

      if (!emailApi.validateEmail(formData.fromEmail)) {
        setError('Invalid email address');
        return;
      }

      // Update formData with selected branch ID for branch-level settings
      const updatedFormData = {
        ...formData,
        providerType,
        zeptoApiKey,
        zeptoDomain,
        branchId: settingsType === 'branch' ? selectedBranchId : null
      };

      if (editingId) {
        await emailApi.updateSmtpSettings(editingId, updatedFormData);
        setSuccess('SMTP settings updated successfully');
      } else {
        await emailApi.createSmtpSettings(updatedFormData);
        setSuccess('SMTP settings created successfully');
      }

      await fetchSmtpSettings();
      handleCancel();
    } catch (error) {
      console.error('Error saving SMTP settings:', error);
      setError('Failed to save SMTP settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete these SMTP settings?')) {
      try {
        await emailApi.deleteSmtpSettings(id);
        await fetchSmtpSettings();
        setSuccess('SMTP settings deleted successfully');
      } catch (error) {
        console.error('Error deleting SMTP settings:', error);
        setError('Failed to delete SMTP settings');
      }
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setError(null);
      
      const testData = {
        smtpHost: formData.smtpHost,
        smtpPort: formData.smtpPort,
        smtpSecure: formData.smtpSecure,
        smtpUsername: formData.smtpUsername,
        smtpPassword: formData.smtpPassword
      };

      const result = await emailApi.testSmtpConnection(testData);
      
      if (result.success) {
        setSuccess('SMTP connection test successful!');
      } else {
        setError(`SMTP connection test failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Error testing SMTP connection:', error);
      setError('Failed to test SMTP connection');
    } finally {
      setTestingConnection(false);
    }
  };

  const getPortSuggestions = (host: string) => {
    const hostLower = host.toLowerCase();
    if (hostLower.includes('gmail')) return { port: 587, secure: false };
    if (hostLower.includes('outlook') || hostLower.includes('hotmail')) return { port: 587, secure: false };
    if (hostLower.includes('yahoo')) return { port: 587, secure: false };
    return { port: 587, secure: false };
  };

  const handleHostChange = (host: string) => {
    const suggestions = getPortSuggestions(host);
    setFormData(prev => ({
      ...prev,
      smtpHost: host,
      smtpPort: suggestions.port,
      smtpSecure: suggestions.secure
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-0 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Email Settings</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <CheckIcon className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <p className="text-sm text-green-600">{success}</p>
                </div>
              </div>
            </div>
          )}

          {/* Global Email Setting Toggle */}
          <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900 mb-1">
                  Document Module Email Setting
                </h4>
                <p className="text-xs text-gray-600">
                  {useGlobalEmailSetting 
                    ? 'Using global email settings (same as login/verification emails)' 
                    : 'Using organization custom email settings'}
                </p>
              </div>
              <button
                onClick={handleToggleGlobalEmailSetting}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                  useGlobalEmailSetting ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    useGlobalEmailSetting ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              <p className="font-medium mb-1">Toggle OFF (Use Global):</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Document request emails use global .env ZeptoMail settings</li>
                <li>Same settings as login/verification emails</li>
              </ul>
              <p className="font-medium mb-1 mt-3">Toggle ON (Use Custom):</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Document request emails use organization-level SMTP/ZeptoMail settings</li>
                <li>Configure your own email credentials below</li>
              </ul>
            </div>
          </div>

          {/* Settings Type Selector */}
          {!isEditing && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-4">
                  <h4 className="text-md font-medium text-gray-900">SMTP Configurations</h4>
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => {
                        setSettingsType('organization');
                        setSelectedBranchId(null);
                      }}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        settingsType === 'organization'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Organization Level
                    </button>
                    <button
                      onClick={() => {
                        setSettingsType('branch');
                        setSelectedBranchId(currentBranch?.id || null);
                      }}
                      disabled={!currentBranch || isViewingAllBranches}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                        settingsType === 'branch'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      } ${(!currentBranch || isViewingAllBranches) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Branch Level
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleCreateNew}
                  disabled={settingsType === 'branch' && (!currentBranch || isViewingAllBranches)}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add New Configuration
                </button>
              </div>

              {/* Settings Type Description */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                  {settingsType === 'organization' ? (
                    <>
                      <strong>Organization-level settings</strong> are shared across all branches. 
                      These are used when no branch-specific settings are configured.
                    </>
                  ) : (
                    <>
                      <strong>Branch-level settings</strong> are specific to individual branches 
                      and will override organization-level settings for those branches.
                    </>
                  )}
                </p>
              </div>

              {(() => {
                // Filter settings based on current type selection
                const filteredSettings = smtpSettings.filter(setting => {
                  if (settingsType === 'organization') {
                    return !setting.branchId; // Organization-level settings have branchId = null
                  } else {
                    return setting.branchId === currentBranch?.id; // Branch-level settings
                  }
                });

                if (filteredSettings.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <p className="text-gray-500">
                        No {settingsType === 'organization' ? 'organization-level' : 'branch-level'} SMTP configurations found
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {filteredSettings.map((settings) => (
                      <div
                        key={settings.id}
                        className={`p-4 border rounded-lg ${
                          settings.isActive ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h5 className="text-sm font-medium text-gray-900">
                                {settings.fromName} &lt;{settings.fromEmail}&gt;
                              </h5>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                {settings.providerType === 'zeptomail' ? 'ZeptoMail' : 'SMTP'}
                              </span>
                              {settings.isActive && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Active
                                </span>
                              )}
                              {settings.branchId ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Branch: {branches.find(b => b.id === settings.branchId)?.name || 'Unknown Branch'}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Organization Level
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {settings.providerType === 'zeptomail' ? (
                                `ZeptoMail Domain: ${settings.zeptoDomain || 'N/A'}`
                              ) : (
                                `${settings.smtpHost}:${settings.smtpPort} (${settings.smtpSecure ? 'SSL' : 'TLS'})`
                              )}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Created: {new Date(settings.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            <PermissionGate module="emails" permission="edit">
                              <button
                                onClick={() => handleEdit(settings)}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                              >
                                Edit
                              </button>
                            </PermissionGate>
                            <PermissionGate module="emails" permission="delete">
                              <button
                                onClick={() => handleDelete(settings.id)}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Delete
                              </button>
                            </PermissionGate>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Form */}
          {isEditing && (
            <div>
              {/* Settings Type Info */}
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">
                  {settingsType === 'organization' ? 'Organization-Level SMTP Settings' : 'Branch-Level SMTP Settings'}
                </h4>
                <div className="text-xs text-blue-700">
                  {settingsType === 'organization' ? (
                    <p>These settings will be shared across all branches and used as fallback when no branch-specific settings exist.</p>
                  ) : (
                    <p>These settings will be specific to the selected branch and will override organization-level settings for that branch.</p>
                  )}
                </div>
              </div>

              {/* Common SMTP Settings Info */}
              <div className="mb-6 bg-gray-50 border border-gray-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-gray-800 mb-2">Common SMTP Settings</h4>
                <div className="text-xs text-gray-700 space-y-1">
                  <p><strong>Gmail:</strong> smtp.gmail.com:587 (TLS) or smtp.gmail.com:465 (SSL)</p>
                  <p><strong>Outlook/Hotmail:</strong> smtp-mail.outlook.com:587 (TLS)</p>
                  <p><strong>Yahoo:</strong> smtp.mail.yahoo.com:587 (TLS) or smtp.mail.yahoo.com:465 (SSL)</p>
                  <p><strong>Custom:</strong> Check with your email provider for SMTP settings</p>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
              
              {/* Provider Type Selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Provider *
                </label>
                <select
                  value={providerType}
                  onChange={(e) => setProviderType(e.target.value as 'smtp' | 'zeptomail')}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="smtp">SMTP (Gmail, Outlook, etc.)</option>
                  <option value="zeptomail">ZeptoMail</option>
                </select>
              </div>
              
              {/* Branch Selector for Branch-Level Settings */}
              {settingsType === 'branch' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-yellow-800 mb-2">Select Branch</h4>
                  <div className="text-xs text-yellow-700 mb-3">
                    Choose which branch these SMTP settings should be applied to.
                  </div>
                  <select
                    value={selectedBranchId || ''}
                    onChange={(e) => setSelectedBranchId(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    required
                  >
                    <option value="">Select a branch...</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} {branch.isHeadOffice ? '(Head Office)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {/* Show SMTP fields only if provider is SMTP */}
              {providerType === 'smtp' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SMTP Host *
                      </label>
                      <input
                        type="text"
                        value={formData.smtpHost}
                        onChange={(e) => handleHostChange(e.target.value)}
                        placeholder="smtp.gmail.com"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required={providerType === 'smtp'}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter the SMTP server hostname (e.g., smtp.gmail.com for Gmail, smtp.outlook.com for Outlook)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Port *
                      </label>
                      <input
                        type="number"
                        value={formData.smtpPort}
                        onChange={(e) => handleInputChange('smtpPort', parseInt(e.target.value))}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required={providerType === 'smtp'}
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.smtpSecure}
                        onChange={(e) => handleInputChange('smtpSecure', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Use SSL</span>
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SMTP Username *
                      </label>
                      <input
                        type="text"
                        value={formData.smtpUsername}
                        onChange={(e) => handleInputChange('smtpUsername', e.target.value)}
                        placeholder="your-email@gmail.com"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required={providerType === 'smtp'}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SMTP Password *
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.smtpPassword}
                          onChange={(e) => handleInputChange('smtpPassword', e.target.value)}
                          placeholder="Your email password"
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pr-10"
                          required={providerType === 'smtp'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Show ZeptoMail fields only if provider is ZeptoMail */}
              {providerType === 'zeptomail' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZeptoMail API Key *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={zeptoApiKey}
                        onChange={(e) => setZeptoApiKey(e.target.value)}
                        placeholder="Your ZeptoMail API Key"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pr-10"
                        required={providerType === 'zeptomail'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Get your API key from ZeptoMail dashboard
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZeptoMail Domain *
                    </label>
                    <input
                      type="text"
                      value={zeptoDomain}
                      onChange={(e) => setZeptoDomain(e.target.value)}
                      placeholder="leadshive.bzyfy.com"
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required={providerType === 'zeptomail'}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Your ZeptoMail verified domain
                    </p>
                  </div>
                </div>
              )}

              {/* Remove the old SMTP fields that are now conditionally shown above */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    value={formData.smtpUsername}
                    onChange={(e) => handleInputChange('smtpUsername', e.target.value)}
                    placeholder="your-email@gmail.com"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Your email address (e.g., your-email@gmail.com)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.smtpPassword}
                      onChange={(e) => handleInputChange('smtpPassword', e.target.value)}
                      placeholder="Your email password or app password"
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Email *
                  </label>
                  <input
                    type="email"
                    value={formData.fromEmail}
                    onChange={(e) => handleInputChange('fromEmail', e.target.value)}
                    placeholder="sender@example.com"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Name *
                  </label>
                  <input
                    type="text"
                    value={formData.fromName}
                    onChange={(e) => handleInputChange('fromName', e.target.value)}
                    placeholder="Your Name"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Set as active configuration</span>
                </label>
              </div>

              {/* Test Connection */}
              <div className="pt-4 border-t border-gray-200">
                <PermissionGate module="emails" permission="edit">
                  <button
                    type="button"
                    onClick={handleTestConnection}
                    disabled={testingConnection || !formData.smtpHost || !formData.smtpUsername || !formData.smtpPassword}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {testingConnection ? 'Testing...' : 'Test Connection'}
                  </button>
                </PermissionGate>
              </div>
            </form>
            </div>
          )}
        </div>

        {/* Footer */}
        {isEditing && (
          <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <PermissionGate module="emails" permission="create">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
            </PermissionGate>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailSettingsModal;
